import { Dialog, Transition } from '@headlessui/react';
import { RefreshCcw } from 'lucide-react';
import { Fragment, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import Button from '../Common/Button';
import Loading from '../Common/Loading';

import { AssigneeDetails } from './ScheduleTestsTable';

const AssigneeDialog = ({
  openAssigneesDialog,
  setOpenAssigneesDialog,
  selectedAssignees,
  handleClickRefresh,
  isRefreshing,
  testStatus
}: {
  openAssigneesDialog: boolean;
  setOpenAssigneesDialog: (val: boolean) => void;
  selectedAssignees: AssigneeDetails[];
  handleClickRefresh: () => void;
  isRefreshing: boolean;
  testStatus: string;
}) => {
  const navigate = useNavigate();
  const isOngoingTest = testStatus === 'ONGOING';
  const handleViewResults = (user: AssigneeDetails) => {
    navigate(`/results/${user?.userTestId}/${user?.userId}`);
  };

  const formatDate = (date: string | null) =>
    date ? new Date(date)?.toLocaleString() : '—';

  useEffect(() => {
    if (!isOngoingTest) return;
    if (!openAssigneesDialog) return;

    const intervalId = setInterval(() => {
      handleClickRefresh();
    }, 5000);

    return () => clearInterval(intervalId);
  }, [openAssigneesDialog, handleClickRefresh]);

  return (
    <Transition appear show={openAssigneesDialog} as={Fragment}>
      <Dialog
        as='div'
        className='relative z-50'
        onClose={setOpenAssigneesDialog}
      >
        <Transition.Child
          as={Fragment}
          enter='ease-out duration-300'
          enterFrom='opacity-0'
          enterTo='opacity-100'
          leave='ease-in duration-200'
          leaveFrom='opacity-100'
          leaveTo='opacity-0'
        >
          <div className='fixed inset-0 bg-black/30' />
        </Transition.Child>

        <div className='fixed inset-0 overflow-y-auto'>
          <div className='flex min-h-full items-center justify-center p-4'>
            <Transition.Child
              as={Fragment}
              enter='ease-out duration-300'
              enterFrom='opacity-0 scale-95'
              enterTo='opacity-100 scale-100'
              leave='ease-in duration-200'
              leaveFrom='opacity-100 scale-100'
              leaveTo='opacity-0 scale-95'
            >
              <Dialog.Panel className='w-full max-w-6xl transform rounded-2xl bg-white p-6 align-middle shadow-xl transition-all'>
                <Dialog.Title className='flex justify-between text-lg font-medium'>
                  <div className='flex items-center gap-2'>
                    {!isOngoingTest ? 'Assignees' : 'Live'}{' '}
                    {isOngoingTest && (
                      <span className='relative flex h-4 w-4'>
                        <span className='absolute h-full w-full animate-ping rounded-full border-2 border-green-500'></span>
                        <span className='relative left-[2px] top-[1.5px] h-3 w-3 rounded-full bg-green-600'></span>
                      </span>
                    )}
                  </div>

                  <button
                    onClick={handleClickRefresh}
                    className='flex items-center gap-2 text-sm'
                    disabled={isRefreshing}
                  >
                    {isRefreshing ? 'Refreshing...' : 'Refresh'}
                    <RefreshCcw
                      size={18}
                      className={`text-primary ${
                        isRefreshing ? 'animate-spin' : ''
                      }`}
                    />
                  </button>
                </Dialog.Title>

                <div className='mt-4 min-h-80 overflow-x-auto'>
                  <table className='min-w-full rounded-md border border-gray-200'>
                    {/* TABLE HEADER */}
                    <thead className='bg-gray-100'>
                      <tr>
                        <th className='border px-4 py-2 text-left'>Name</th>
                        <th className='border px-4 py-2 text-left'>Email</th>
                        <th className='border px-4 py-2 text-left'>Mobile</th>
                        <th className='border px-4 py-2 text-left'>Status</th>
                        <th className='border px-4 py-2 text-left'>
                          Started On
                        </th>
                        <th className='border px-4 py-2 text-left'>
                          Submitted On
                        </th>
                        <th className='border px-4 py-2 text-left'>Action</th>
                      </tr>
                    </thead>

                    {/* TABLE BODY */}
                    <tbody>
                      {isRefreshing ? (
                        /* LOADER */
                        <tr>
                          <td colSpan={7} className='py-6'>
                            <div className='flex items-center justify-center'>
                              <Loading text='Loading ...' />
                            </div>
                          </td>
                        </tr>
                      ) : selectedAssignees.length > 0 ? (
                        /* DATA ROWS */
                        selectedAssignees.map(user => (
                          <tr key={user.userId} className='hover:bg-gray-50'>
                            <td className='border px-4 py-2 text-sm'>
                              {user.name ?? '-'}
                            </td>
                            <td className='border px-4 py-2 text-sm'>
                              {user.emailId ?? '-'}
                            </td>
                            <td className='border px-4 py-2 text-sm'>
                              {user.mobileNumber ?? '-'}
                            </td>

                            <td className='border px-4 py-2 text-sm'>
                              <span
                                className={`rounded px-2 py-1 text-xs font-medium
                      ${
                        user.testStatus === 'COMPLETED'
                          ? 'bg-green-100 text-green-700'
                          : user.testStatus === 'STARTED'
                            ? 'bg-yellow-100 text-yellow-700'
                            : 'bg-gray-100 text-gray-600'
                      }`}
                              >
                                {user.testStatus === 'NOT_YET'
                                  ? 'NOT YET'
                                  : user.testStatus}
                              </span>
                            </td>

                            <td className='border px-4 py-2 text-sm'>
                              {user?.startedOn
                                ? new Date(
                                    `${user?.startedOn}z`
                                  )?.toLocaleString()
                                : '-'}
                            </td>

                            <td className='border px-4 py-2 text-sm'>
                              {user?.submittedOn
                                ? new Date(
                                    `${user?.submittedOn}z`
                                  )?.toLocaleString()
                                : '-'}
                            </td>

                            <td className='border px-4 py-2 text-sm'>
                              {user.testStatus === 'COMPLETED' ? (
                                <Button
                                  label='View Results'
                                  handleClickBtn={() => handleViewResults(user)}
                                  variant='contained'
                                  classes='!text-sm !px-2 !py-2 !w-[120px] !ml-0 !hover:bg-secondary'
                                />
                              ) : (
                                <span className='text-gray-400'>—</span>
                              )}
                            </td>
                          </tr>
                        ))
                      ) : (
                        /* EMPTY STATE */
                        <tr>
                          <td
                            colSpan={7}
                            className='py-4 text-center text-gray-500'
                          >
                            No assignees found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <div className='flex items-center justify-center'>
                  <button
                    className='mt-6 rounded-md bg-blue-600 px-4 py-2 text-white'
                    onClick={() => setOpenAssigneesDialog(false)}
                  >
                    Close
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default AssigneeDialog;
